import React from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { Button, BUTTON_THEME, Modal } from '@st-operations/uikit/src/v2'

import downloadCSV from '_lib/downloadCSV'

import {
  coverageCsv,
  coverageCsvName,
  mappingLabel,
  noPushReason,
  number,
  partnerStatusOf,
  prefillFor,
  toPushIdsOf,
  when,
} from './format'
import './styles.scss'

// the modal renders at the end of the page, outside #app
const MODAL_CLASSES = {
  content: 'coverage-modal',
  innerWrapper: 'coverage-modal__inner',
  childWrapper: 'coverage-modal__body',
}

// enough to see what is there - the CSV has every one
const SHOWN = 200

// "c353c173", or what was looked for when the space's own ID is not it -
// Adelphic's "adelphic", looked for by our global then US ID
const idOf = (audience) =>
  audience.segment_id || (audience.looked_for ? audience.looked_for.join(', then ') : '-')

// `column`: one more, beside the segment ID - { title, value(audience) }
const AudienceList = ({ title, audiences, showStatus, column }) => (
  <If condition={audiences?.length}>
    <div className="coverage-modal__list">
      <div className="coverage-modal__list-title">
        {title} ({number(audiences.length)})
      </div>
      <table className="coverage-table">
        <thead>
          <tr>
            <th>Audience</th>
            <th>Segment ID</th>
            <If condition={showStatus}>
              <th>Status there</th>
            </If>
            <If condition={column}>
              <th>{column.title}</th>
            </If>
          </tr>
        </thead>
        <tbody>
          <For each="audience" of={audiences.slice(0, SHOWN)}>
            <tr key={audience._id}>
              <td>{audience.name}</td>
              <td className="is-mono">{idOf(audience)}</td>
              <If condition={showStatus}>
                <td>{audience.status}</td>
              </If>
              <If condition={column}>
                <td>{column.value(audience)}</td>
              </If>
            </tr>
          </For>
        </tbody>
      </table>
      <If condition={audiences.length > SHOWN}>
        <div className="coverage-modal__note">
          The first {SHOWN} - the CSV has all {number(audiences.length)}.
        </div>
      </If>
    </div>
  </If>
)

AudienceList.propTypes = {
  title: PropTypes.string.isRequired,
  audiences: PropTypes.arrayOf(PropTypes.object),
  showStatus: PropTypes.bool,
  column: PropTypes.shape({ title: PropTypes.string, value: PropTypes.func }),
}

AudienceList.defaultProps = {
  audiences: [],
  showStatus: false,
  column: null,
}

// The audiences behind one distribution's coverage in one partner: those it
// does not have, those it has but not active, and those with no ID to look
// for. The ones it does not have can go straight into a new push - picked,
// for a person to check and run - or out as a CSV, to clear with the partner
// first.
const CoverageModal = ({ row, taxonomy, isOpen, onClose }) => {
  const history = useHistory()
  // those the partner does not have - the API's not_synced
  const missing = row.not_synced || []
  // what a push sends: those the partner does not have, none on a blocklist -
  // the same as the coverage page's "Push unsynced"
  const toPush = toPushIdsOf(row)
  const reason = noPushReason(row)
  // whether any of those missing are left off the allowlist too
  const anyOff = missing.some((audience) => audience.off_allowlist)

  const startPush = () =>
    history.push({
      pathname: `/taxonomies/${taxonomy.slug}/api-integrations/new`,
      state: { prefill: prefillFor(row, toPush) },
    })

  return (
    <Modal isOpen={isOpen} onClose={onClose} classes={MODAL_CLASSES}>
      <section className="coverage-modal__content">
        <div className="coverage-modal__head">
          <h2 className="coverage-modal__title">{row.partner.name} coverage</h2>
          <div className="coverage-modal__meta">
            {taxonomy.name} · mapping ID {mappingLabel(row)} ·{' '}
            {row.destinations.map((d) => d.name).join(', ')}
          </div>
          <div className="coverage-modal__meta">
            {number(row.counts.synced)} of {number(row.counts.audiences)} synced, from{' '}
            {row.partner.name}&apos;s list of {when(row.snapshot?.taken_at)}
            <If condition={row.counts.pushed_since}>
              {' '}
              and {number(row.counts.pushed_since)} pushed since
            </If>
          </div>
        </div>

        <div className="coverage-modal__actions">
          <If condition={toPush.length && !reason}>
            <Button theme={BUTTON_THEME.PRIMARY} onClick={startPush}>
              Push these {number(toPush.length)} to {row.partner.name}
            </Button>
          </If>
          <Button
            theme={BUTTON_THEME.SECONDARY}
            onClick={() => downloadCSV(coverageCsv(row), coverageCsvName(taxonomy.name, row))}
          >
            Download CSV
          </Button>
        </div>
        <If condition={toPush.length && reason}>
          <div className="coverage-modal__note">{reason}</div>
        </If>
        <If condition={toPush.length && !reason && row.push_mapping_id !== row.mapping_id}>
          <div className="coverage-modal__note">
            Pushed by their {row.push_mapping_id} ID - {row.partner.name} numbers the{' '}
            {row.mapping_id} IDs itself.
          </div>
        </If>

        <AudienceList
          title={`Missing from ${row.partner.name}`}
          audiences={missing}
          column={
            anyOff
              ? { title: 'Allowlist', value: (a) => (a.off_allowlist ? 'not on it' : 'on it') }
              : null
          }
        />
        <AudienceList title="Inactive" audiences={row.inactive} showStatus />
        <If condition={row.unchecked?.length}>
          <div className="coverage-modal__note coverage-modal__note--list">
            {row.partner.name} is asked about our IDs once a night, for branded taxonomies only -
            these weren&apos;t asked about yet: added since, or in a taxonomy that isn&apos;t
            branded.
          </div>
        </If>
        <AudienceList title="Not checked yet" audiences={row.unchecked} />
        <If condition={row.not_allowed?.length}>
          <div className="coverage-modal__note coverage-modal__note--list">
            In the taxonomy, but left off the distribution&apos;s allowlist (Segment ID Whitelist),
            so not sent to {row.destinations.map((d) => d.name).join(', ')}. Add their segment IDs
            to it if they should be sent - and push any {row.partner.name} doesn&apos;t have.
          </div>
        </If>
        <AudienceList
          title="Not on the allowlist"
          audiences={row.not_allowed}
          column={{ title: `In ${row.partner.name}`, value: partnerStatusOf }}
        />
        <AudienceList title={`No ${row.mapping_id} ID`} audiences={row.no_id} />
      </section>
    </Modal>
  )
}

CoverageModal.propTypes = {
  row: PropTypes.shape({
    partner: PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string }),
    mapping_id: PropTypes.string,
    predictor_percentile_override: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    push_mapping_id: PropTypes.string,
    destinations: PropTypes.arrayOf(
      PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string })
    ),
    counts: PropTypes.object,
    snapshot: PropTypes.object,
    not_synced: PropTypes.arrayOf(PropTypes.object),
    inactive: PropTypes.arrayOf(PropTypes.object),
    unchecked: PropTypes.arrayOf(PropTypes.object),
    not_allowed: PropTypes.arrayOf(PropTypes.object),
    no_id: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  taxonomy: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default CoverageModal
